import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  IconButton,
  ListSubheader,
  Checkbox,
  InputAdornment
} from '@mui/material';
import {
  Edit,
  ExpandMore,
  AccountBalance,
  Security,
  Assessment,
  Description,
  Link as LinkIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { api as axios } from '../../api';
import url from '../../serverUrl';
import { logoWrapper } from '../../utils';
import TreasuryLockStatusFields from './TreasuryLockStatusFields';

import {
  PRIMARY_CHAIN_CODES,
  REDEMPTION_TYPE_CODES,
  PROVENANCE_TYPE_CODES,
  COLLATERAL_TYPE_CODES,
  RISK_PROFILE_CODES,
  RESERVE_TYPE_CODES,
  BRIDGED_CHAINS_CODES,
  COLLATERALIZATION_TYPE_CODES
} from './stableCodeDictionaryNew';

// Flat list of all reserve type options for label lookup
const ALL_RESERVE_OPTIONS = [
  { code: '170101', label: 'Verified Gold Reserve' },
  { code: '170102', label: 'Verified Silver Reserve' },
  { code: '170103', label: 'Verified Platinum Reserve' },
  { code: '170104', label: 'Verified Palladium Reserve' },
  { code: '170201', label: 'Verified Copper Reserve' },
  { code: '170202', label: 'Verified Nickel Reserve' },
  { code: '170203', label: 'Verified Aluminum Reserve' },
  { code: '170301', label: 'Verified WTI Crude Oil Reserve' },
  { code: '170302', label: 'Verified Brent Crude Oil Reserve' },
  { code: '170303', label: 'Verified Natural Gas Reserve' },
  { code: '170304', label: 'Verified Gasoline Reserve' },
  { code: '170401', label: 'BTC Reserve' },
  { code: '170402', label: 'ETH Reserve' },
  { code: '170403', label: 'SOL Reserve' },
  { code: '170404', label: 'PECU Reserve' }
];

function EditStablecoinToken({ token, open, setOpen }) {
  /* ===================== STATE ===================== */
  const [form, setForm] = useState(token);
  const [status, setStatus] = useState(token?.status ?? false);
  const [logo, setLogo] = useState(token?.logo ?? '');

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    const normalizeToLabelString = (value, optionsList) => {
      if (!value) return '';
      let items = [];
      // Fully unwrap any number of JSON encoding layers
      let unwrapped = value;
      if (typeof unwrapped === 'string') {
        let prev = null;
        while (prev !== unwrapped) {
          prev = unwrapped;
          try {
            unwrapped = JSON.parse(unwrapped);
          } catch (e) {
            break;
          }
        }
      }
      if (Array.isArray(unwrapped)) {
        items = unwrapped.map(String);
      } else if (typeof unwrapped === 'string') {
        items = unwrapped
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return items
        .map((item) => {
          // If it's already a label (not a code), return as-is
          const byCode = optionsList.find((o) => o.code === item);
          if (byCode) return byCode.label;
          const byLabel = optionsList.find((o) => o.label === item);
          return byLabel ? byLabel.label : item;
        })
        .join(', ');
    };

    const normalizedToken = {
      ...token,
      reserve_type: normalizeToLabelString(
        token?.reserve_type,
        ALL_RESERVE_OPTIONS
      ),
      bridged_chains: normalizeToLabelString(
        token?.bridged_chains,
        BRIDGED_CHAINS_CODES[0]?.options || []
      )
    };

    setForm(normalizedToken);
    setStatus(token?.status ?? false);
    setLogo(token?.logo ?? '');
  }, [token]);

  /* ===================== EARLY RETURN ===================== */
  if (token?.type !== 'STABLECOIN' && token?.type !== 'XMG') return null;

  /* ===================== HANDLERS ===================== */
  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  // For single-select: resolve code → label before storing
  const handleSelectChange = (field, optionsList) => (e) => {
    const code = e.target.value;
    const item = optionsList.find((o) => o.code === code);
    setForm({ ...form, [field]: item ? item.label : code });
  };

  const handleMultiSelectChange = (field, optionsList) => (e) => {
    const selectedCodes =
      typeof e.target.value === 'string'
        ? e.target.value.split(',')
        : e.target.value;
    // Convert codes → labels, then join as comma-separated string
    const labels = selectedCodes.map((code) => {
      const item = optionsList.find((o) => o.code === code);
      return item ? item.label : code;
    });
    setForm({ ...form, [field]: labels.join(', ') });
  };

  const handleClearMultiSelect = (field) => (e) => {
    // Stop the Select from opening when clicking the clear button
    e.stopPropagation();
    setForm({ ...form, [field]: '' });
  };

  const saveToken = () => {
    axios
      .put(`${url}/crypto/updateToken`, { ...form, status })
      .then((res) => alert(res.data.message || 'Token updated successfully'))
      .catch((err) => alert(JSON.stringify(err)));
  };

  const uploadLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('image', file);
    fd.append('fileName', file.name);

    axios.post(`${url}/hootdex/token-logo-upload`, fd).then((res) => {
      if (res.data.status === 'ok') {
        const img = `${url}/hootdex/images/${res.data.fileName}`;
        setLogo(img);
        setForm({ ...form, logo: img });
      }
    });
  };

  // Split comma-separated label strings back into arrays for the Select components
  const reserveTypes = form?.reserve_type
    ? form.reserve_type
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];
  const bridgedChains = form?.bridged_chains
    ? form.bridged_chains
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  /* ===================== JSX ===================== */
  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', py: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight={600}>
            Edit Stablecoin Token
          </Typography>
          <Chip
            label={status ? 'Active' : 'Inactive'}
            color={status ? 'success' : 'default'}
            size="small"
          />
        </Box>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: '#f8f9fa', p: 0 }}>
        <Box p={3}>
          {/* Logo & Status Section */}
          <Paper
            elevation={0}
            sx={{ p: 3, mb: 3, bgcolor: 'white', borderRadius: 2 }}
          >
            <Box display="flex" alignItems="center" gap={3}>
              <Box position="relative">
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '2px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fafafa'
                  }}
                >
                  <img
                    src={logoWrapper(logo, token)}
                    width={100}
                    alt={form?.name}
                    style={{ objectFit: 'contain' }}
                  />
                </Box>
                <input
                  hidden
                  id="stable-logo"
                  type="file"
                  onChange={uploadLogo}
                />
                <label htmlFor="stable-logo">
                  <IconButton
                    component="span"
                    size="small"
                    sx={{
                      position: 'absolute',
                      bottom: 5,
                      right: 5,
                      bgcolor: 'success.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'success.dark' }
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </label>
              </Box>

              <Box flex={1}>
                <Typography variant="h6" gutterBottom>
                  {form?.name || 'Stablecoin Token'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Symbol: {form?.symbol || 'N/A'}
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mt={2}>
                  <Typography variant="body2" fontWeight={500}>
                    Status:
                  </Typography>
                  <Switch
                    checked={status}
                    onChange={(e) => setStatus(e.target.checked)}
                  />
                  <Chip
                    label={status ? 'Active' : 'Inactive'}
                    color={status ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </Paper>

          {/* ========== ACCORDION SECTIONS ========== */}

          {/* Identity Section */}
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Description color="primary" />
                <Typography fontWeight={600}>Identity Information</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  fullWidth
                  label="Stablecoin Name"
                  value={form?.name ?? ''}
                  onChange={handleChange('name')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Symbol"
                  value={form?.symbol ?? ''}
                  onChange={handleChange('symbol')}
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Issuer"
                  value={form?.issuer ?? ''}
                  onChange={handleChange('issuer')}
                  size="small"
                  helperText="Name of the issuing organization"
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Description"
                  value={form?.description ?? ''}
                  onChange={handleChange('description')}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Reserve & Collateral Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <AccountBalance color="primary" />
                <Typography fontWeight={600}>Reserve & Collateral</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                {/* Reserve Type — fixed label lookup + clear button */}
                <FormControl fullWidth size="small">
                  <InputLabel>Reserve Type (Multi-Select)</InputLabel>
                  <Select
                    multiple
                    value={reserveTypes}
                    onChange={handleMultiSelectChange(
                      'reserve_type',
                      ALL_RESERVE_OPTIONS
                    )}
                    label="Reserve Type (Multi-Select)"
                    endAdornment={
                      reserveTypes.length > 0 ? (
                        <InputAdornment position="end" sx={{ mr: 2 }}>
                          <IconButton
                            size="small"
                            onClick={handleClearMultiSelect('reserve_type')}
                            title="Clear all"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                    renderValue={(selected) => {
                      if (!selected.length) return 'Select reserves...';
                      // selected is already an array of label strings
                      return (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {selected.map((label, idx) => (
                            <Chip key={idx} label={label} size="small" />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    {/* Precious Metals */}
                    <ListSubheader
                      sx={{ fontWeight: 600, color: 'success.main' }}
                    >
                      Precious Metals
                    </ListSubheader>
                    {[
                      { code: '170101', label: 'Verified Gold Reserve' },
                      { code: '170102', label: 'Verified Silver Reserve' },
                      { code: '170103', label: 'Verified Platinum Reserve' },
                      { code: '170104', label: 'Verified Palladium Reserve' }
                    ].map((o) => (
                      <MenuItem key={o.code} value={o.label} sx={{ pl: 4 }}>
                        <Checkbox
                          checked={reserveTypes.indexOf(o.label) > -1}
                        />
                        <Typography variant="body2">{o.label}</Typography>
                      </MenuItem>
                    ))}

                    {/* Industrial Metals */}
                    <ListSubheader
                      sx={{ fontWeight: 600, color: 'success.main' }}
                    >
                      Industrial Metals
                    </ListSubheader>
                    {[
                      { code: '170201', label: 'Verified Copper Reserve' },
                      { code: '170202', label: 'Verified Nickel Reserve' },
                      { code: '170203', label: 'Verified Aluminum Reserve' }
                    ].map((o) => (
                      <MenuItem key={o.code} value={o.label} sx={{ pl: 4 }}>
                        <Checkbox
                          checked={reserveTypes.indexOf(o.label) > -1}
                        />
                        <Typography variant="body2">{o.label}</Typography>
                      </MenuItem>
                    ))}

                    {/* Energy Reserves */}
                    <ListSubheader
                      sx={{ fontWeight: 600, color: 'success.main' }}
                    >
                      Energy Reserves
                    </ListSubheader>
                    {[
                      {
                        code: '170301',
                        label: 'Verified WTI Crude Oil Reserve'
                      },
                      {
                        code: '170302',
                        label: 'Verified Brent Crude Oil Reserve'
                      },
                      { code: '170303', label: 'Verified Natural Gas Reserve' },
                      { code: '170304', label: 'Verified Gasoline Reserve' }
                    ].map((o) => (
                      <MenuItem key={o.code} value={o.label} sx={{ pl: 4 }}>
                        <Checkbox
                          checked={reserveTypes.indexOf(o.label) > -1}
                        />
                        <Typography variant="body2">{o.label}</Typography>
                      </MenuItem>
                    ))}

                    {/* Digital Assets */}
                    <ListSubheader
                      sx={{ fontWeight: 600, color: 'success.main' }}
                    >
                      Digital Assets
                    </ListSubheader>
                    {[
                      { code: '170401', label: 'BTC Reserve' },
                      { code: '170402', label: 'ETH Reserve' },
                      { code: '170403', label: 'SOL Reserve' },
                      { code: '170404', label: 'PECU Reserve' }
                    ].map((o) => (
                      <MenuItem key={o.code} value={o.label} sx={{ pl: 4 }}>
                        <Checkbox
                          checked={reserveTypes.indexOf(o.label) > -1}
                        />
                        <Typography variant="body2">{o.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <SelectBlock
                  label="Collateral Architecture"
                  value={form?.collateral_architecture}
                  onChange={handleSelectChange(
                    'collateral_architecture',
                    COLLATERAL_TYPE_CODES[0]?.options || []
                  )}
                  options={COLLATERAL_TYPE_CODES[0]?.options || []}
                  size="small"
                />
                <SelectBlock
                  label="Collateralization Type"
                  value={form?.collateralization_type}
                  onChange={handleSelectChange(
                    'collateralization_type',
                    COLLATERALIZATION_TYPE_CODES[0]?.options || []
                  )}
                  options={COLLATERALIZATION_TYPE_CODES[0]?.options || []}
                  size="small"
                />
                <SelectBlock
                  label="Provenance"
                  value={form?.provenance}
                  onChange={handleSelectChange(
                    'provenance',
                    PROVENANCE_TYPE_CODES[0]?.options || []
                  )}
                  options={PROVENANCE_TYPE_CODES[0]?.options || []}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Valuation & Transparency Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Assessment color="primary" />
                <Typography fontWeight={600}>
                  Valuation & Transparency
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Valuation Method"
                  value={form?.valuation_method}
                  onChange={handleSelectChange('valuation_method', [
                    { code: '173101', label: 'Spot-Pegged' },
                    { code: '173102', label: 'NAV-Based' },
                    { code: '173103', label: 'Oracle-Based' },
                    { code: '173104', label: 'Smart-Contract-Calculated' },
                    { code: '173105', label: 'Reserve-Weighted Average' },
                    { code: '173106', label: 'Forward Curve' },
                    { code: '173107', label: 'Reserve-Adjusted Price' }
                  ])}
                  options={[
                    { code: '173101', label: 'Spot-Pegged' },
                    { code: '173102', label: 'NAV-Based' },
                    { code: '173103', label: 'Oracle-Based' },
                    { code: '173104', label: 'Smart-Contract-Calculated' },
                    { code: '173105', label: 'Reserve-Weighted Average' },
                    { code: '173106', label: 'Forward Curve' },
                    { code: '173107', label: 'Reserve-Adjusted Price' }
                  ]}
                  size="small"
                />
                <SelectBlock
                  label="Pegging Mechanism"
                  value={form?.pegging_mechanism}
                  onChange={handleSelectChange('pegging_mechanism', [
                    { code: '176101', label: 'Fixed Peg (1:1)' },
                    { code: '176102', label: 'Basket Peg' },
                    { code: '176103', label: 'Algorithmic Peg' },
                    { code: '176104', label: 'Floating Peg' },
                    { code: '176105', label: 'Commodity Peg' },
                    { code: '176106', label: 'Currency Peg' },
                    { code: '176107', label: 'Smart-Contract-Enforced Peg' }
                  ])}
                  options={[
                    { code: '176101', label: 'Fixed Peg (1:1)' },
                    { code: '176102', label: 'Basket Peg' },
                    { code: '176103', label: 'Algorithmic Peg' },
                    { code: '176104', label: 'Floating Peg' },
                    { code: '176105', label: 'Commodity Peg' },
                    { code: '176106', label: 'Currency Peg' },
                    { code: '176107', label: 'Smart-Contract-Enforced Peg' }
                  ]}
                  size="small"
                />
                <SelectBlock
                  label="Transparency Level"
                  value={form?.transparency}
                  onChange={handleSelectChange('transparency', [
                    { code: '177101', label: 'On-Chain Reserves' },
                    { code: '177102', label: 'Daily Reserve Attestation' },
                    { code: '177103', label: 'Monthly Reserve Attestation' },
                    { code: '177104', label: 'Smart-Contract Verifiable' },
                    { code: '177105', label: 'Audited Reserves' },
                    { code: '177106', label: 'ZKP Reserve Proof' }
                  ])}
                  options={[
                    { code: '177101', label: 'On-Chain Reserves' },
                    { code: '177102', label: 'Daily Reserve Attestation' },
                    { code: '177103', label: 'Monthly Reserve Attestation' },
                    { code: '177104', label: 'Smart-Contract Verifiable' },
                    { code: '177105', label: 'Audited Reserves' },
                    { code: '177106', label: 'ZKP Reserve Proof' }
                  ]}
                  size="small"
                />
                <SelectBlock
                  label="Treasury Management"
                  value={form?.treasury_management}
                  onChange={handleSelectChange('treasury_management', [
                    { code: '181101', label: 'Issuer-Managed Treasury' },
                    { code: '181102', label: 'DAT-Managed Treasury' },
                    { code: '181103', label: 'Smart-Contract Autonomous' },
                    { code: '181104', label: 'Hybrid Treasury Management' },
                    { code: '181105', label: 'Third-Party Custodian' }
                  ])}
                  options={[
                    { code: '181101', label: 'Issuer-Managed Treasury' },
                    { code: '181102', label: 'DAT-Managed Treasury' },
                    { code: '181103', label: 'Smart-Contract Autonomous' },
                    { code: '181104', label: 'Hybrid Treasury Management' },
                    { code: '181105', label: 'Third-Party Custodian' }
                  ]}
                  size="small"
                />
                <TreasuryLockStatusFields tokenType="STABLECOIN" tokenId={form?.id} />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Risk & Redemption Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Security color="primary" />
                <Typography fontWeight={600}>Risk & Redemption</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Risk Profile"
                  value={form?.risk_profile}
                  onChange={handleSelectChange(
                    'risk_profile',
                    RISK_PROFILE_CODES[0]?.options || []
                  )}
                  options={RISK_PROFILE_CODES[0]?.options || []}
                  size="small"
                />
                <SelectBlock
                  label="Redemption Type"
                  value={form?.redemption_type}
                  onChange={handleSelectChange(
                    'redemption_type',
                    REDEMPTION_TYPE_CODES[0]?.options || []
                  )}
                  options={REDEMPTION_TYPE_CODES[0]?.options || []}
                  size="small"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Blockchain & Network Section */}
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: '#f5f5f5' }}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <LinkIcon color="primary" />
                <Typography fontWeight={600}>Blockchain & Network</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <SelectBlock
                  label="Primary Chain"
                  value={form?.primary_chain}
                  onChange={handleSelectChange(
                    'primary_chain',
                    PRIMARY_CHAIN_CODES[0]?.options || []
                  )}
                  options={PRIMARY_CHAIN_CODES[0]?.options || []}
                  size="small"
                />

                {/* Bridged Chains — with clear button */}
                <FormControl fullWidth size="small">
                  <InputLabel>Bridged Chains (Multi-Select)</InputLabel>
                  <Select
                    multiple
                    value={bridgedChains}
                    onChange={handleMultiSelectChange(
                      'bridged_chains',
                      BRIDGED_CHAINS_CODES[0]?.options || []
                    )}
                    label="Bridged Chains (Multi-Select)"
                    endAdornment={
                      bridgedChains.length > 0 ? (
                        <InputAdornment position="end" sx={{ mr: 2 }}>
                          <IconButton
                            size="small"
                            onClick={handleClearMultiSelect('bridged_chains')}
                            title="Clear all"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                    renderValue={(selected) => {
                      if (!selected.length) return 'Select chains...';
                      return (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {selected.map((label, idx) => (
                            <Chip key={idx} label={label} size="small" />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    {BRIDGED_CHAINS_CODES[0]?.options?.map((chain) => (
                      <MenuItem key={chain.code} value={chain.label}>
                        <Checkbox
                          checked={bridgedChains.indexOf(chain.label) > -1}
                        />
                        <Typography variant="body2">{chain.label}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ bgcolor: 'white', px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}
      >
        <Button onClick={() => setOpen(false)} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={saveToken}
          sx={{
            bgcolor: 'success.main',
            '&:hover': { bgcolor: 'success.dark' },
            px: 4
          }}
        >
          Save Stablecoin
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ===================== HELPERS ===================== */
const SelectBlock = ({
  label,
  value,
  onChange,
  options,
  size = 'medium',
  helperText
}) => {
  // value stored in form may be a label string already; find matching option either way
  const resolvedValue = (() => {
    if (!value) return '';
    const byLabel = options.find((o) => o.label === value);
    if (byLabel) return byLabel.label;
    const byCode = options.find((o) => o.code === value);
    return byCode ? byCode.label : value;
  })();

  return (
    <FormControl fullWidth size={size}>
      <InputLabel>{label}</InputLabel>
      <Select value={resolvedValue} label={label} onChange={onChange}>
        {options.map((o) => (
          <MenuItem key={o.code} value={o.label}>
            <Typography variant="body2">{o.label}</Typography>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, ml: 1.5 }}
        >
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default EditStablecoinToken;
